import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ClearIcon from '@mui/icons-material/Clear';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LaunchIcon from '@mui/icons-material/Launch';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { useUser } from '@/context/UserContext';
import { environment } from '@/config/environment';
import { getProductAvailability, updateInventoryItemStatus } from '@/services/inventory/inventoryService';
import { StatusSelect } from '@/components/shared/StatusSelect';
import { AvailableRailcarsDialog } from './AvailableRailcarsDialog';
import { RecalculateInventoryDialog } from './RecalculateInventoryDialog';
import { InventoryStatusNoteDialog } from './InventoryStatusNoteDialog';
import { InventoryGradingPage } from './InventoryGradingPage';
import './inventory-management.css';

type TabName = 'availability' | 'exceptions' | 'railcar';
type Notice = 'Filter' | 'Loading' | 'No Records' | 'Error' | 'Timeout';
type Filter = { columnName: string; value: string };
type Sort = { columnName: string; value: 'ASC' | 'DESC' };
type InventoryRow = Record<string, any> & { rowId: string };

const FIXED_COLUMN_WIDTHS: Record<string, number> = {
  'Type': 104,
  'Order #': 100,
  'Notes': 40,
  'Line Item Status': 126,
};

const COLUMN_WEIGHTS: Record<string, number> = {
  'Type': 3.5,
  'Order #': 5,
  'Base Material': 7,
  'Sales Material': 7,
  'Supply Point': 5.5,
  'Ship-to': 5,
  'Transportation Planning Date': 12,
  'Planned Load Date': 7.5,
  'Shipping Condition': 7,
  'Order Quantity': 6.5,
  'Available Inventory': 7,
  'Proj. Inv. On Load Date': 8,
  'Selected UoM': 5.5,
  'UoM': 4,
  'Line Item Status': 12,
  'Railcar ID': 7,
  'Batch ID': 7,
  'Gross Weight': 7,
};

const HEADER_LINES: Record<string, string[]> = {
  'Supply Point': ['Supply', 'Point'],
  'Transportation Planning Date': ['Transportation', 'Planning Date'],
  'Planned Load Date': ['Planned', 'Load Date'],
  'Shipping Condition': ['Shipping', 'Condition'],
  'Order Quantity': ['Order', 'Quantity'],
  'Available Inventory': ['Available', 'Inventory'],
  'Proj. Inv. On Load Date': ['Proj. Inv.', 'On Load Date'],
  'Selected UoM': ['Selected', 'UoM'],
};

const FILTER_FIELDS = [
  ['OrderType', 'Order Type'], ['OrderNumber', 'Order #'], ['OrderStatus', 'Order Status'], ['BaseMaterial', 'Base Material Code'], ['BaseMaterialDescription', 'Base Material Description'], ['PlantCode', 'Supply Point Code'], ['PlantDescription', 'Supply Point Name'], ['ShipToPlantCode', 'Ship-to Code'], ['ShipToPlantName', 'Ship-to Name'], ['ShipToPlantCity', 'Ship-to City'], ['ShipToPlantState', 'Ship-to State/Prov'], ['ShipToPlantPostalCode', 'Ship-to Postal Code'], ['ShipToPlantCountry', 'Ship-to Country'], ['SlannedTransportationDate', 'Transportation Planning Date'], ['SlannedLoadDate', 'Planned Load Date'], ['PlannedDeliveryDate', 'Requested Delivery Date'], ['ShippingCondition', 'Shipping Condition'], ['ModeOfTransportation', 'Mode Of Transportation'], ['OrderUnitOfMeasure', 'Order Unit Of Measure'], ['ItemStatus', 'Line Item Status'], ['RailCarId', 'Railcar ID'], ['BatchId', 'Batch ID'], ['OrderCreatorName', 'Order Creator'], ['OrderCreationDate', 'Order Creation Date'],
].sort((a, b) => a[1].localeCompare(b[1]));
const SORT_FIELDS = [
  ['OrderType', 'Order Type'], ['OrderNumber', 'Order #'], ['OrderStatus', 'Order Status'],
  ['BaseMaterial', 'Base Material'], ['BaseMaterialDescription', 'Base Material Description'],
  ['PlantCode', 'Supply Point Code'], ['PlantDescription', 'Supply Point Name'],
  ['ShipToPlantCode', 'Ship-to Code'], ['ShipToPlantName', 'Ship-to Name'],
  ['ShipToPlantCity', 'Ship-to City'], ['ShipToPlantState', 'Ship-to State/Prov'],
  ['ShipToPlantPostalCode', 'Ship-to Postal Code'], ['ShipToPlantCountry', 'Ship-to Country'],
  ['PlannedTransportationDate', 'Transportation Planning Date'], ['PlannedLoadDate', 'Planned Load Date'],
  ['PlannedDeliveryDate', 'Requested Delivery Date'], ['ShippingCondition', 'Shipping Condition'],
  ['ModeOfTransportation', 'Mode Of Transportation'], ['ItemStatus', 'Item Status'],
  ['RailCarId', 'Railcar ID'], ['BatchId', 'Batch ID'], ['OrderCreatorName', 'Order Creator Name'],
  ['OrderCreationDate', 'Order Creation Date'], ['PlanningUnit', 'Planning Unit'],
  ['ProductSubGroup', 'Product Sub Group'], ['Division', 'Division'], ['ProductFamily', 'Product Family'],
].sort((a, b) => a[1].localeCompare(b[1]));
const hasDuplicateFields = (items: Filter[]) => new Set(items.map((item) => item.columnName)).size !== items.length;
const text = (value: unknown) => value === null || value === undefined || value === '' ? '-' : String(value);
const short = (value: unknown) => { const valueText = text(value); return valueText.length > 12 ? `${valueText.slice(0, 12)}...` : valueText; };
const dateText = (value: unknown) => { if (!value) return '-'; const date = new Date(value as string); return Number.isNaN(date.getTime()) ? text(value) : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }); };
const numberText = (value: unknown, maximumFractionDigits = 3) => typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits }) : text(value);
const rowKey = (row: Record<string, any>, index: number) => `${row.source ?? ''}|${row.orderNumber ?? row.OrderNumber ?? ''}|${row.itemNumber ?? row.ItemNumber ?? index}`;
const firstPopulatedText = (...values: unknown[]) => values.find((value) => typeof value === 'string' && value.trim() !== '');
const adaptRow = (raw: Record<string, any>, index: number): InventoryRow => ({
  ...raw, rowId: rowKey(raw, index),
  baseMaterialCode: raw.baseMaterialCode ?? raw.baseMaterial,
  orderQty: raw.orderQty ?? raw.orderQuantity,
  orderUOM: raw.orderUOM ?? raw.orderUnitOfMeasure,
  landedInventory: raw.landedInventory ?? raw.landedQuantity,
  projectedInventory: raw.projectedInventory ?? raw.projectedInventoryOnLoadDate,
  salesMaterialDescription: raw.salesMaterialDescription ?? raw.materialDescription,
  salesMaterialCode: raw.salesMaterialCode ?? raw.materialCode,
  orderCreator: raw.orderCreator ?? raw.orderCreatorName,
  transportationPlanningDate: raw.transportationPlanningDate ?? raw.plannedTransportationDate,
  planningLoadDate: raw.planningLoadDate ?? raw.plannedLoadDate,
  selectedUOM: raw.selectedUOM ?? raw.unitOfMeasure,
  shipToCode: raw.shipToCode ?? raw.shipToPlantCode,
  shipToDescription: raw.shipToDescription ?? raw.shipToNumber,
  shipToPlantDescription: firstPopulatedText(raw.shipToPlantName, raw.shipToPlantDescription, raw.shipToDescription, raw.shipToNumber),
  customerCity: firstPopulatedText(raw.shipToPlantCity, raw.customerCity),
  customerCountry: firstPopulatedText(raw.shipToPlantCountry, raw.customerCountry),
  internalNotes: firstPopulatedText(raw.internalNotes, raw.notes),
  railcarId: raw.railcarId ?? raw.railCarId,
  grossWeight: raw.grossWeight ?? raw.maxGrossWeight,
  criticalCustomer: raw.criticalCustomer === true || raw.criticalCustomer === 'Y',
  plantCode: raw.plantCode ?? raw.supplyLocation,
  itemStatus: raw.itemStatus ?? raw.productStatus,
  UOM: raw.UOM ?? raw.unitOfMeasure,
});
const updatedTimestamp = (value: unknown) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value !== 'string' || !value.trim()) return NaN;
  const timestamp = value.trim();
  return Date.parse(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/i.test(timestamp) ? `${timestamp}Z` : timestamp);
};
const ShippingIcon = ({ mode }: { mode: unknown }) => {
  switch (String(mode ?? '').toUpperCase()) {
    case 'TRUCK': return <LocalShippingIcon />;
    case 'RAIL': return <TrainIcon />;
    case 'MCT': case 'MARINE BULK': case 'MBL': return <DirectionsBoatIcon />;
    default: return null;
  }
};

export const InventoryManagementPage: React.FC = () => {
  const view = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('view');
  return view === 'grading'
    ? <InventoryGradingPage />
    : <AvailabilityInventoryManagementPage />;
};

const AvailabilityInventoryManagementPage: React.FC = () => {
  const { userData } = useUser();
  const [tab, setTab] = useState<TabName>('availability'); const [uom, setUom] = useState('KG'); const [uoms, setUoms] = useState<string[]>(['KG']); const [rows, setRows] = useState<InventoryRow[]>([]); const [count, setCount] = useState(0); const [page, setPage] = useState(0); const [pageSize, setPageSize] = useState(25); const [notice, setNotice] = useState<Notice>('Filter'); const [filters, setFilters] = useState<Filter[]>([]); const [sorts, setSorts] = useState<Sort[]>([{ columnName: 'PlannedTransportationDate', value: 'ASC' }]); const [selected, setSelected] = useState<Set<string>>(new Set()); const [expanded, setExpanded] = useState<Set<string>>(new Set()); const [statuses, setStatuses] = useState<string[]>([]); const [dialog, setDialog] = useState<'filter' | 'sort' | 'status' | null>(null); const [statusValue, setStatusValue] = useState('--'); const [draftFilters, setDraftFilters] = useState<Filter[]>([]); const [draftSorts, setDraftSorts] = useState<Sort[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [recalculateOpen, setRecalculateOpen] = useState(false);
  const [railcarRow, setRailcarRow] = useState<InventoryRow | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [pendingStatusNote, setPendingStatusNote] = useState<{ row: InventoryRow; status: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestSequence = useRef(0);
  const activeLoad = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const [hideMOT, setHideMOT] = useState(false);
  const cancelLoad = useCallback(() => {
    requestSequence.current += 1;
    activeLoad.current?.abort();
    activeLoad.current = null;
  }, []);

  useEffect(() => {
    mounted.current = true;
    cancelLoad();
    setRows([]); setCount(0); setPage(0); setNotice('Filter'); setHasLoaded(false);
    setSelected(new Set()); setExpanded(new Set()); setDialog(null);
    setRecalculateOpen(false);
    setPendingStatusNote(null);
    setRailcarRow(null); setStatusError(false); setStatusUpdating(false);
    setLoadError(null);
    setUoms(['KG']); setStatuses([]);
    const controller = new AbortController();
    const config = { headers: jsonHeaders(), signal: controller.signal };
    void axiosInstance.get('/unitofmeasure', { ...config, params: { Source: userData.region || userData.product } }).then(({ data }) => {
      if (controller.signal.aborted) return;
      const values = (data?.datas ?? []).map((item: any) => item.uom).filter(Boolean);
      setUoms(Array.from(new Set(['KG', ...values])));
    }).catch(() => undefined);
    void axiosInstance.get('/cyberorderstatus', config).then(({ data }) => {
      if (!controller.signal.aborted) setStatuses((data ?? []).map((item: any) => item.orderStatus).filter(Boolean));
    }).catch(() => undefined);
    return () => { mounted.current = false; controller.abort(); cancelLoad(); };
  }, [cancelLoad, userData.product, userData.region]);

  const loadRows = useCallback(async (nextPage = page, nextFilters = filters, nextSorts = sorts, nextUom = uom, nextPageSize = pageSize, nextTab = tab) => {
    if (!mounted.current) return;
    cancelLoad();
    const sequence = requestSequence.current;
    const controller = new AbortController();
    activeLoad.current = controller;
    setPage(nextPage); setNotice('Loading'); setRows([]); setCount(0);
    setLoadError(null);
    setSelected(new Set()); setExpanded(new Set());
    const startedAt = Date.now();
    try {
      const data = await getProductAvailability({
        linesPerPage: nextPageSize, pageNumber: nextPage + 1,
        railcarOnly: nextTab === 'railcar', unitOfMeasure: nextUom,
        showExceptions: nextTab === 'exceptions', filters: nextFilters, sorting: nextSorts,
      }, controller.signal);
      if (controller.signal.aborted || sequence !== requestSequence.current) return;
      const nextRows = (data?.data ?? []).map(adaptRow);
      setRows(nextRows); setCount(Number(data?.count ?? nextRows.length));
      setHasLoaded(true); setNotice(nextRows.length ? 'Filter' : 'No Records');
    } catch (error: any) {
      if (controller.signal.aborted || sequence !== requestSequence.current) return;
      const isTimeout = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
        || /timeout|timed out/i.test(String(error?.response?.data ?? error));
      const elapsedSeconds = ((Date.now() - startedAt) / 1000).toFixed(1);
      const timeoutSeconds = Number(error?.config?.timeout ?? environment.inventoryTimeout) / 1000;
      const status = error?.response?.status;
      const detail = typeof error?.response?.data === 'string' ? error.response.data
        : error?.response?.data?.detail ?? error?.response?.data?.title;
      const message = typeof detail === 'string' && detail.trim() && !/<\s*(?:!doctype|html)/i.test(detail)
        ? ` ${detail.slice(0, 500)}` : '';
      setLoadError(`Inventory request failed after ${elapsedSeconds}s. ${status ? `HTTP ${status}.` : 'No HTTP response was available to the browser.'} Error: ${error?.code ?? 'unknown'}. Client timeout: ${timeoutSeconds}s.${message}`);
      setNotice(isTimeout ? 'Timeout' : 'Error');
    } finally {
      if (sequence === requestSequence.current) activeLoad.current = null;
    }
  }, [cancelLoad, filters, page, pageSize, sorts, tab, uom, userData.product, userData.region]);
  const changeStatuses = async (items: InventoryRow[], itemStatus: string, reportError = true) => {
    if (statusUpdating) return false;
    const sequence = requestSequence.current;
    setStatusUpdating(true); setStatusError(false);
    try {
      const results = await Promise.allSettled(items.map((row) => updateInventoryItemStatus({
        orderNumber: row.orderNumber,
        itemNumber: row.itemNumber,
        itemStatus,
        baseMaterial: row.baseMaterialCode,
        batchId: row.batchId,
        railCarId: row.railcarId,
        location: row.location ?? row.plantCode,
        plantCode: row.plantCode,
        notes: row.notes,
        unassignBatch: row.unassignBatch,
      })));
      if (results.some((result) => result.status === 'rejected')) throw new Error('Status update failed');
      if (mounted.current && sequence === requestSequence.current) await loadRows();
      return true;
    } catch {
      if (reportError && mounted.current && sequence === requestSequence.current) setStatusError(true);
      return false;
    } finally {
      if (mounted.current) setStatusUpdating(false);
    }
  };
  const applyFilters = () => { setFilters(draftFilters); setDialog(null); void loadRows(0, draftFilters, sorts); };
  const applySorts = () => { setSorts(draftSorts); setDialog(null); void loadRows(0, filters, draftSorts); };
  const toggleTab = (_: React.SyntheticEvent, value: TabName) => {
    if (value === tab) return;
    cancelLoad(); setTab(value); setPage(0); setDialog(null);
    setRailcarRow(null); setStatusError(false);
    setLoadError(null);
    if (value === 'railcar' || tab === 'railcar') {
      setHideMOT(true);
      setFilters([]); setSorts([]); setDraftFilters([]); setDraftSorts([]);
      setNotice('Filter'); setRows([]); setCount(0); setHasLoaded(false);
      setSelected(new Set()); setExpanded(new Set());
    } else {
      void loadRows(0, filters, sorts, uom, pageSize, value);
    }
  };
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.rowId)); const toggleSelected = (id: string) => setSelected((old) => { const next = new Set(old); next.has(id) ? next.delete(id) : next.add(id); return next; }); const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.rowId))); const toggleExpanded = (id: string) => setExpanded((old) => { const next = new Set(old); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const formatType = (value: unknown) => { const valueText = text(value); return valueText.includes('(') ? valueText.slice(valueText.lastIndexOf('(') + 1).replace(')', '') : valueText; };
  const lastCalculated = useMemo(() => {
    if (!rows.length) return '...';
    const timestamps = rows.map((row) => updatedTimestamp(row.lastUpdated));
    if (timestamps.some((timestamp) => !Number.isFinite(timestamp))) return 'undetermined, please recalculate';
    return new Date(Math.min(...timestamps)).toLocaleString();
  }, [rows]);
  const columns = tab === 'railcar' ? ['Type', 'Order #', 'Base Material', 'Supply Point', 'Ship-to', 'Transportation Planning Date', 'Planned Load Date', 'Shipping Condition', 'Order Quantity', 'UoM', 'Notes', 'Railcar ID', 'Batch ID', 'Gross Weight'] : ['Type', 'Order #', 'Base Material', 'Sales Material', 'Supply Point', 'Ship-to', 'Transportation Planning Date', 'Planned Load Date', 'Shipping Condition', 'Order Quantity', 'Available Inventory', 'Proj. Inv. On Load Date', 'Selected UoM', 'Notes', 'Line Item Status'];
  const columnWeightTotal = columns.reduce((total, column) => total + (FIXED_COLUMN_WIDTHS[column] ? 0 : COLUMN_WEIGHTS[column]), 0);
  const utilityColumnCount = tab === 'railcar' ? 1 : 2;
  const utilityColumnWidth = (tab === 'railcar' ? 24 : 60) + columns.reduce((total, column) => total + (FIXED_COLUMN_WIDTHS[column] ?? 0), 0);
  const columnCount = columns.length + utilityColumnCount;
  const collapseAll = rows.length === 0 || expanded.size === rows.length;
  const paginationEmpty = rows.length === 0 || count === 0;
  const lastPage = Math.max(0, Math.ceil(count / pageSize) - 1);
  const changePage = (nextPage: number) => { setPage(nextPage); void loadRows(nextPage); };
  const openFilters = () => { setDraftFilters(filters); setDialog('filter'); };

  return (
    <Box className="inventory-page">
      <Box className="im-page-header">
        <Typography component="h1" className="im-page-header-title">Product Availability</Typography>
      </Box>
      <Tabs value={tab} onChange={toggleTab} className="availability-tabs" variant="scrollable" scrollButtons="auto" aria-label="Inventory views">
        <Tab value="availability" label="PRODUCT AVAILABILITY" />
        <Tab value="exceptions" label="EXCEPTIONS" />
        <Tab value="railcar" label="RAILCAR ASSIGNMENT" />
      </Tabs>
      <Box className="availability-content">
        {loadError && <Alert severity="error" onClose={() => setLoadError(null)}>{loadError}</Alert>}
        {statusError && <Alert severity="error" onClose={() => setStatusError(false)}>Unable to update line item status. Refresh the table before retrying; some selected items may have been updated.</Alert>}
        <Box className="availability-toolbar">
          <Box className="toolbar-left">
            <Button variant="contained" disabled={!hasLoaded || notice === 'Loading'} onClick={() => changePage(0)}>REFRESH TABLE</Button>
            <Button variant="contained" disabled={!hasLoaded || notice === 'Loading' || statusUpdating} onClick={() => setRecalculateOpen(true)}>RECALCULATE INVENTORY</Button>
            <Typography>Last Calculated {lastCalculated}</Typography>
          </Box>
          <Box className="toolbar-right">
            <FormControl variant="standard" size="small" className="uom-select">
              <InputLabel id="availability-uom-label">UoM</InputLabel>
              <Select labelId="availability-uom-label" value={uom} onChange={(event) => {
                setUom(event.target.value);
                setPage(0);
                void loadRows(0, filters, sorts, event.target.value);
              }}>
                {uoms.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={expanded.size > 0 ? <ExpandMoreIcon /> : <ChevronRightIcon />} onClick={() => {
              const next = expanded.size !== rows.length;
              setExpanded(next ? new Set(rows.map((row) => row.rowId)) : new Set());
            }}>
              {collapseAll ? 'COLLAPSE ALL' : 'EXPAND ALL'}
            </Button>
            {selected.size > 0 && <Button variant="outlined" disabled={statusUpdating} onClick={() => setDialog('status')}>MULTI STATUS CHANGE</Button>}
            <Button className={sorts.length ? 'active-control' : undefined} variant={sorts.length ? 'contained' : 'outlined'} onClick={() => { setDraftSorts(sorts); setDialog('sort'); }}>
              {sorts.length} ACTIVE SORTS
            </Button>
            <Button className={filters.length ? 'active-control' : undefined} variant={filters.length ? 'contained' : 'outlined'} onClick={openFilters}>
              {filters.length ? `${filters.length} ACTIVE FILTERS` : 'FILTER COLUMNS'}
            </Button>
          </Box>
        </Box>
        <Paper className="availability-table-paper" square elevation={0}>
          <table className="availability-table" aria-label="Product availability" aria-busy={notice === 'Loading'}>
            <colgroup>
              <col className="expand-column" />
              {tab !== 'railcar' && <col className="selection-column" />}
              {columns.map((column) => (
                <col key={column} style={{ width: FIXED_COLUMN_WIDTHS[column] ? `${FIXED_COLUMN_WIDTHS[column]}px` : `calc((100% - ${utilityColumnWidth}px) * ${COLUMN_WEIGHTS[column] / columnWeightTotal})` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" aria-label="Expand row" />
                {tab !== 'railcar' && <th scope="col">
                  <Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected} onChange={toggleAll} inputProps={{ 'aria-label': 'Select all rows' }} />
                </th>}
                {columns.map((column) => <th scope="col" key={column} aria-label={column}>
                  {(HEADER_LINES[column] ?? [column]).map((line, index) => <React.Fragment key={line}>
                    {index > 0 && <><br />{' '}</>}{line}
                  </React.Fragment>)}
                </th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.rowId}>
                  <tr>
                    <td className="expand-cell">
                      <IconButton size="small" aria-label={expanded.has(row.rowId) ? 'Collapse row' : 'Expand row'} aria-expanded={expanded.has(row.rowId)} onClick={() => toggleExpanded(row.rowId)}>
                        {expanded.has(row.rowId) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                      </IconButton>
                    </td>
                    {tab !== 'railcar' && <td><Checkbox checked={selected.has(row.rowId)} onChange={() => toggleSelected(row.rowId)} inputProps={{ 'aria-label': `Select order ${text(row.orderNumber)}` }} /></td>}
                    <td className="single-line-cell">{formatType(row.orderType)}</td>
                    <td className="single-line-cell" title={text(row.orderNumber)}>{short(row.orderNumber)}</td>
                    <td title={text(row.baseMaterialCode)}>{row.source?.toLowerCase() === 'g9p' ? short(row.baseMaterialDescription) : short(row.baseMaterialCode)}</td>
                    {tab !== 'railcar' && <td>{short(row.salesMaterialDescription)}</td>}
                    <td>{text(row.plantCode)}</td>
                    <td>{text(row.shipToCode)}</td>
                    <td>{dateText(row.transportationPlanningDate)}</td>
                    <td>{dateText(row.planningLoadDate)}</td>
                    <td className="shipping-cell">
                      <ShippingIcon mode={row.modeOfTransportation} /> {text(row.shippingCondition)}
                    </td>
                    <td>{numberText(row.orderQty)}</td>
                    {tab !== 'railcar' && <>
                      <td>{numberText(row.landedInventory)}</td>
                      <td>{numberText(row.projectedInventory)}</td>
                      <td>{text(row.UOM)}</td>
                    </>}
                    {tab === 'railcar' && <td>{text(row.UOM)}</td>}
                    <td>{row.internalNotes ? <AssignmentIcon fontSize="small" /> : ''}</td>
                    {tab === 'railcar' ? <>
                      <td className="link-cell"><Button size="small" onClick={() => setRailcarRow(row)} aria-label={`View available railcars for order ${text(row.orderNumber)}`}>{row.railcarId || 'VIEW RAILCARS'}</Button></td>
                      <td className="link-cell">{text(row.batchId)}</td>
                      <td>{numberText(row.grossWeight)}</td>
                    </> : (
                      <td className="status-cell">
                        <StatusSelect
                          truncateText={false}
                          fontSize={11}
                          value={row.itemStatus ?? ''}
                          options={statuses}
                          disabled={statusUpdating}
                          ariaLabel={`Line item status for order ${text(row.orderNumber)}`}
                          isOptionDisabled={(status) => !status.includes('IM-') && status !== 'OC-New'}
                          onChange={(status) => {
                            if (status === row.itemStatus) return;
                            if (status === 'IM-Recycle' || status === 'IM-Reject') setPendingStatusNote({ row, status });
                            else void changeStatuses([row], status);
                          }}
                        />
                      </td>
                    )}
                  </tr>
                  {expanded.has(row.rowId) && (
                    <tr className="detail-row">
                      <td colSpan={columnCount}>
                        <Box className="detail-grid">
                          <Box className="detail-column" role="group" aria-label="Order details">
                            <DetailField label="Order Creator" value={row.orderCreator} />
                            <DetailField label="Order Creation Date" value={row.orderCreationDate ? dateText(row.orderCreationDate) : ''} />
                          </Box>
                          <Box className="detail-column" role="group" aria-label="Material and original quantity">
                            <DetailField label="Sales Material Code" value={String(row.salesMaterialCode ?? '').replace(/^0+/, '')} />
                            <DetailField label="Base Material Code" value={String(row.baseMaterialCode ?? '').replace(/^0+/, '')} />
                            <Box className="detail-quantity">
                              <DetailField label="Original Order Qty" value={numberText(row.originalOrderQty, 6)} />
                              <DetailField label="Original Order UoM" value={row.originalOrderUOM} />
                            </Box>
                          </Box>
                          <Box className="detail-column" role="group" aria-label="Customer details">
                            <DetailField label="Ship-to Name" value={row.shipToPlantDescription ?? row.shipToDescription} />
                            <DetailField label="Ship-to Country" value={row.customerCountry} />
                            <DetailField label="Ship-to City" value={row.customerCity} />
                            <DetailField label="Customer Service Class" value={row.customerServiceClass} />
                            <FormControlLabel className="detail-critical-customer" control={<Checkbox checked={row.criticalCustomer} disabled />} label="Critical Customer" />
                          </Box>
                          <Box className="detail-column" role="group" aria-label="Projected inventory and notes">
                            <Button className="detail-projected-inventory" variant="outlined" startIcon={<LaunchIcon />} disabled>VIEW FULL PROJECTED INVENTORY</Button>
                            <TextField
                              className="detail-notes"
                              variant="standard"
                              label="Internal Notes"
                              value={row.internalNotes ?? ''}
                              multiline
                              rows={7}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ readOnly: true }}
                            />
                          </Box>
                        </Box>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {rows.length === 0 && (
              <tfoot>
                <tr>
                  <td colSpan={columnCount}>
                    <NoticeContent notice={notice} onRetry={() => changePage(0)} onFilter={openFilters} />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          <Box className="paginator" role="navigation" aria-label="Table pagination">
            <Typography component="label" id="availability-page-size-label">Items per page:</Typography>
            <Select className="page-size-select" size="small" variant="outlined" labelId="availability-page-size-label" value={pageSize} onChange={(event) => { const nextPageSize = Number(event.target.value); setPageSize(nextPageSize); void loadRows(0, filters, sorts, uom, nextPageSize); }}>
              {[25, 50, 100].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </Select>
            <Typography className="pagination-range">{!paginationEmpty ? `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, count)} of ${count}` : '0 of 0'}</Typography>
            <Box className="pagination-actions">
              <IconButton aria-label="First page" disabled={paginationEmpty || page === 0} onClick={() => changePage(0)}><FirstPageIcon /></IconButton>
              <IconButton aria-label="Previous page" disabled={paginationEmpty || page === 0} onClick={() => changePage(page - 1)}><ChevronLeftIcon /></IconButton>
              <IconButton aria-label="Next page" disabled={paginationEmpty || page >= lastPage} onClick={() => changePage(page + 1)}><ChevronRightIcon /></IconButton>
              <IconButton aria-label="Last page" disabled={paginationEmpty || page >= lastPage} onClick={() => changePage(lastPage)}><LastPageIcon /></IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>
      <FilterDialog open={dialog === 'filter'} fields={FILTER_FIELDS.filter(([key]) => !hideMOT || key !== 'ModeOfTransportation')} filters={draftFilters} setFilters={setDraftFilters} onClose={() => setDialog(null)} onApply={applyFilters} />
      <SortDialog open={dialog === 'sort'} fields={SORT_FIELDS.filter(([key]) => !hideMOT || key !== 'ModeOfTransportation')} sorts={draftSorts} setSorts={setDraftSorts} onClose={() => setDialog(null)} onApply={applySorts} />
      <StatusDialog open={dialog === 'status'} statuses={statuses} value={statusValue} setValue={setStatusValue} onClose={() => setDialog(null)} onApply={() => {
        if (statusValue === '--') return;
        setDialog(null);
        void changeStatuses(rows.filter((row) => selected.has(row.rowId)), statusValue);
      }} />
      {railcarRow && <AvailableRailcarsDialog
        source={railcarRow.source || userData.region || userData.product}
        orderNumber={railcarRow.orderNumber} itemNumber={railcarRow.itemNumber}
        plantCode={railcarRow.plantCode} materialCode={railcarRow.baseMaterialCode}
        unitOfMeasure={railcarRow.UOM || uom} onClose={() => setRailcarRow(null)}
      />}
      {pendingStatusNote && <InventoryStatusNoteDialog
        orderNumber={pendingStatusNote.row.orderNumber}
        status={pendingStatusNote.status}
        onClose={() => setPendingStatusNote(null)}
        onUpdateStatus={() => changeStatuses([pendingStatusNote.row], pendingStatusNote.status, false)}
      />}
      {recalculateOpen && <RecalculateInventoryDialog
        source={userData.product?.toLowerCase() === 'chemicals' ? 'G9P' : userData.region}
        filters={filters}
        tab={tab}
        onClose={() => setRecalculateOpen(false)}
        onSuccess={() => { setRecalculateOpen(false); void loadRows(0); }}
        onEditFilters={() => { setRecalculateOpen(false); openFilters(); }}
      />}
    </Box>
  );
};

const DetailField = ({ label, value }: { label: string; value: unknown }) => <TextField
  className="detail-field"
  variant="standard"
  label={label}
  value={value === null || value === undefined ? '' : String(value)}
  disabled
  InputLabelProps={{ shrink: true }}
/>;
const NoticeContent = ({ notice, onRetry, onFilter }: { notice: Notice; onRetry: () => void; onFilter: () => void }) => {
  const content = notice === 'Loading'
    ? { icon: <CircularProgress variant="determinate" value={100} size={96} />, title: 'Loading table data', message: '' }
    : notice === 'Filter'
      ? { icon: <TuneIcon />, title: 'Filtering required', message: 'To maximize performance please add at least 1 filter to load this table.' }
      : notice === 'No Records'
        ? { icon: <DescriptionIcon />, title: 'No records found', message: 'There are no records that match your current filters. Change or remove filters to see available records.' }
        : { icon: <ClearIcon />, title: 'Table failed to load', message: notice === 'Timeout' ? 'Data for this table took too long to load. Please try again, or edit filters to narrow the results.' : 'An error occured, please try again or refresh your browser. If issues persist contact the OTD admin.' };

  return (
    <Box className="notice-container" role="status" aria-live="polite">
      <Box className={`notice-icon${notice === 'Loading' ? ' notice-icon-loading' : ''}`}>{content.icon}</Box>
      <Typography className="notice-title">{content.title}</Typography>
      {content.message && <Typography className="notice-message">{content.message}</Typography>}
      {notice !== 'Loading' && (
        <Box className="notice-actions">
          {notice === 'Filter' && <Button variant="contained" onClick={onFilter}>ADD FILTER</Button>}
          {(notice === 'No Records' || notice === 'Timeout') && <Button variant={notice === 'Timeout' ? 'outlined' : 'contained'} onClick={onFilter}>EDIT FILTERS</Button>}
          {(notice === 'Error' || notice === 'Timeout') && <Button variant="contained" onClick={onRetry}>TRY AGAIN</Button>}
        </Box>
      )}
    </Box>
  );
};
function FilterDialog({ open, fields, filters, setFilters, onClose, onApply }: { open: boolean; fields: string[][]; filters: Filter[]; setFilters: React.Dispatch<React.SetStateAction<Filter[]>>; onClose: () => void; onApply: () => void }) {
  const duplicate = hasDuplicateFields(filters);
  const invalid = duplicate || filters.some((item) => !item.value.trim() || !fields.some(([key]) => key === item.columnName));
  const nextField = fields.find(([key]) => !filters.some((item) => item.columnName === key));
  return (
    <Dialog open={open} onClose={onClose} className="availability-dialog availability-query-dialog" fullWidth maxWidth="md">
      <DialogTitle>Filter table columns</DialogTitle>
      <DialogContent>
        <Typography className="notice">Each column can only be selected once. To search for multiple items in a single field, separate each search term with a comma.</Typography>
        {duplicate && <Typography color="error">Cannot have duplicate values.</Typography>}
        {filters.map((filter, index) => (
          <Box className="dialog-row" key={`${index}-${filter.columnName}`}>
            <FormControl variant="standard" size="small">
              <InputLabel id={`availability-filter-field-${index}`}>Field to filter</InputLabel>
              <Select labelId={`availability-filter-field-${index}`} value={filter.columnName} onChange={(event) => setFilters((old) => old.map((item, i) => i === index ? { ...item, columnName: event.target.value } : item))}>
                {fields.map(([key, label]) => <MenuItem key={key} value={key} disabled={filters.some((item, i) => i !== index && item.columnName === key)}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField variant="standard" label="Keyword filter" value={filter.value} error={!filter.value.trim()} helperText={!filter.value.trim() ? 'Must enter a value.' : undefined} onChange={(event) => setFilters((old) => old.map((item, i) => i === index ? { ...item, value: event.target.value } : item))} />
            <IconButton aria-label="Delete filter" onClick={() => setFilters((old) => old.filter((_, i) => i !== index))}><DeleteIcon /></IconButton>
          </Box>
        ))}
        <Button className="query-add-button" startIcon={<AddIcon />} disabled={!nextField} onClick={() => { if (nextField) setFilters((old) => [...old, { columnName: nextField[0], value: '' }]); }}>ADD FILTER</Button>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>CANCEL</Button><Button variant="contained" disabled={invalid} onClick={onApply}>APPLY FILTER</Button></DialogActions>
    </Dialog>
  );
}
function SortDialog({ open, fields, sorts, setSorts, onClose, onApply }: { open: boolean; fields: string[][]; sorts: Sort[]; setSorts: React.Dispatch<React.SetStateAction<Sort[]>>; onClose: () => void; onApply: () => void }) {
  const duplicate = hasDuplicateFields(sorts);
  const invalid = duplicate || sorts.some((item) => !fields.some(([key]) => key === item.columnName));
  const nextField = fields.find(([key]) => !sorts.some((item) => item.columnName === key));
  return (
    <Dialog open={open} onClose={onClose} className="availability-dialog availability-query-dialog" fullWidth maxWidth="md">
      <DialogTitle>Sort table columns</DialogTitle>
      <DialogContent>
        <Typography className="notice">Sorting will be done in the priority listed below, starting with the first. Use the arrows on the right to adjust the order of the list.</Typography>
        {duplicate && <Typography color="error">Cannot have duplicate values.</Typography>}
        {sorts.map((sort, index) => (
          <Box className="dialog-row" key={`${index}-${sort.columnName}`}>
            <b className="sort-number">{index + 1}</b>
            <FormControl variant="standard" size="small">
              <InputLabel id={`availability-sort-field-${index}`}>Field to sort</InputLabel>
              <Select labelId={`availability-sort-field-${index}`} value={sort.columnName} onChange={(event) => setSorts((old) => old.map((item, i) => i === index ? { ...item, columnName: event.target.value } : item))}>
                {fields.map(([key, label]) => <MenuItem key={key} value={key} disabled={sorts.some((item, i) => i !== index && item.columnName === key)}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl variant="standard" size="small" required>
              <InputLabel id={`availability-sort-direction-${index}`}>Sort direction</InputLabel>
              <Select labelId={`availability-sort-direction-${index}`} value={sort.value} onChange={(event) => setSorts((old) => old.map((item, i) => i === index ? { ...item, value: event.target.value as 'ASC' | 'DESC' } : item))}>
                <MenuItem value="ASC">Ascending (A-Z, 0-9)</MenuItem><MenuItem value="DESC">Descending (Z-A, 9-0)</MenuItem>
              </Select>
            </FormControl>
            <Box className="query-row-actions">
              <IconButton aria-label="Move sort down" disabled={index === sorts.length - 1} onClick={() => setSorts((old) => { const next = [...old]; [next[index + 1], next[index]] = [next[index], next[index + 1]]; return next; })}><ArrowDownwardIcon /></IconButton>
              <IconButton aria-label="Move sort up" disabled={index === 0} onClick={() => setSorts((old) => { const next = [...old]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next; })}><ArrowUpwardIcon /></IconButton>
              <IconButton aria-label="Delete sort" onClick={() => setSorts((old) => old.filter((_, i) => i !== index))}><DeleteIcon /></IconButton>
            </Box>
          </Box>
        ))}
        <Button className="query-add-button" startIcon={<AddIcon />} disabled={!nextField} onClick={() => { if (nextField) setSorts((old) => [...old, { columnName: nextField[0], value: 'DESC' }]); }}>ADD SORT</Button>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>CANCEL</Button><Button variant="contained" disabled={invalid} onClick={onApply}>APPLY SORT</Button></DialogActions>
    </Dialog>
  );
}
function StatusDialog({ open, statuses, value, setValue, onClose, onApply }: { open: boolean; statuses: string[]; value: string; setValue: (value: string) => void; onClose: () => void; onApply: () => void }) { return <Dialog open={open} onClose={onClose} className="availability-dialog"><DialogTitle>Selection Status</DialogTitle><DialogContent><Typography className="notice">Select the status that will be applied to the selected orders.</Typography><FormControl fullWidth><InputLabel>Selection Status</InputLabel><Select value={value} label="Selection Status" onChange={(event) => setValue(event.target.value)}><MenuItem value="--">--</MenuItem>{statuses.map((status) => <MenuItem key={status} value={status} disabled={!status.includes('IM-') && status !== 'OC-New'}>{status}</MenuItem>)}</Select></FormControl></DialogContent><DialogActions><Button onClick={onClose}>CANCEL</Button><Button variant="contained" onClick={onApply}>APPLY STATUS</Button></DialogActions></Dialog>; }
